import { useMemo, useCallback, useRef, useState } from 'react';
import style from './free-cash-flows-by-slot-table.module.scss';
import {
  CashflowRow,
  CashflowsFilterConfig,
  CashflowsSortConfig,
} from 'types/api-responses';
import { snakeCase, startCase } from 'lodash-es';
import { useCashflowsByMonthPaginated } from 'pages/packages/show/evaluation-result/aggregated-free-cash-flow/hooks';
import { apiConfig } from 'types/api-config';
import { subscribe } from 'lib/pusher';

import {
  DataTable,
  DataTableColumnDef,
  ColumnType,
  DataTableColumnMeta,
} from 'components/data-table';
import type { ExportPreset as DataTableExportPreset } from 'components/data-table/types';
import type { ExportPreset } from 'pages/packages/show/eval-3-workspace/types';
import { ColumnFiltersState, SortingState } from '@tanstack/react-table';
import { DataTableSkeleton, InlineNotification } from '@carbon/react';

// ─────────────────────────────────────
// Column Configuration
// ─────────────────────────────────────

/**
 * Column metadata configuration following DataTable documentation.
 * Uses meta.type and meta.format directly as documented.
 */
interface ColumnMetaConfig {
  header?: string;
  type?: ColumnType;
  format?: {
    minDecimalPlaces?: number;
    maxDecimalPlaces?: number;
    unit?: string;
    multiplier?: number;
    currency?: string;
  };
}

/**
 * Configuration for each column's display name and formatting.
 * Follows the DataTable documentation format:
 * - type: 'text' | 'integer' | 'decimal' | 'currency' | 'percent' | 'compact' | 'date' | 'boolean' | 'enum'
 * - format: { maxDecimalPlaces, minDecimalPlaces, unit, multiplier, currency }
 */
const columnConfig: Record<string, ColumnMetaConfig> = {
  // ─── Text columns ───
  formationName: { header: 'Formation', type: 'text' },
  basin: { header: 'Basin', type: 'text' },
  tier: { header: 'Tier', type: 'text' },
  developmentAreaStateAbbreviation: { header: 'State', type: 'text' },
  ltdAssumptionsType: { header: 'LTD Type', type: 'text' },
  ducKind: { header: 'DUC Type', type: 'text' },
  permitKind: { header: 'Permit Type', type: 'text' },
  source: { header: 'Source', type: 'text' },
  slotVolumeSource: { header: 'Volume Source', type: 'text' },
  waterForecastSource: { header: 'Water Forecast Source', type: 'text' },
  wellName: { header: 'Well Name', type: 'text' },
  uwi: { header: 'UWI', type: 'text' },
  developmentAreaName: { header: 'Development Area', type: 'text' },
  developmentAreaId: { header: 'Development Area ID', type: 'text' },
  subtractId: { header: 'Subtract ID', type: 'text' },
  wellStatus: { header: 'Well Status', type: 'text' },
  wellHoleDirection: { header: 'Hole Direction', type: 'text' },
  forecastStatus: { header: 'Forecast Status', type: 'text' },
  type: { header: 'Type', type: 'text' },
  startDateType: { header: 'Start Date Type', type: 'text' },
  effectiveDateType: { header: 'Effective Date Type', type: 'text' },
  discountToDateType: { header: 'Discount To Date Type', type: 'text' },
  priceType: { header: 'Price Type', type: 'text' },
  typeCurveHeaderName: { header: 'Type Curve', type: 'text' },
  uniqueTcId: { header: 'Unique TC ID', type: 'text' },
  uniqueSlotId: { header: 'Unique Slot ID', type: 'text' },
  wellSrpLandingZone: { header: 'SRP Landing Zone', type: 'text' },
  ntdAssumptionsType: { header: 'NTD Type', type: 'text' },
  wellHref: { header: 'Well Link', type: 'text' },
  sourceId: { header: 'Source ID', type: 'text' },
  pdpForecastVersionId: { header: 'PDP Forecast Version', type: 'text' },
  productionVolumeVersionId: {
    header: 'Production Volume Version',
    type: 'text',
  },
  packageEvaluationId: { header: 'Package Evaluation ID', type: 'text' },

  // ─── Date columns ───
  wellPermitDate: { header: 'Permit Date', type: 'date' },
  spudDate: { header: 'Spud Date', type: 'date' },
  wellCompletionDate: { header: 'Completion Date', type: 'date' },
  firstProductionDate: { header: 'First Production Date', type: 'date' },
  shutInDate: { header: 'Shut-In Date', type: 'date' },
  gasTransitionDate: { header: 'Gas Transition Date', type: 'date' },
  waterTransitionDate: { header: 'Water Transition Date', type: 'date' },
  oilTransitionDate: { header: 'Oil Transition Date', type: 'date' },
  startDate: { header: 'Start Date', type: 'date' },
  effectiveDate: { header: 'Effective Date', type: 'date' },
  discountToDate: { header: 'Discount To Date', type: 'date' },
  stripPriceDate: { header: 'Strip Price Date', type: 'date' },

  // ─── Boolean columns ───
  applyLtdHurdleRate: { header: 'LTD Hurdle Rate Applied', type: 'boolean' },
  applyLTDHurdleRate: { header: 'LTD Hurdle Rate Applied', type: 'boolean' },
  applyDucHurdleRate: { header: 'DUC Hurdle Rate Applied', type: 'boolean' },
  applyDUCHurdleRate: { header: 'DUC Hurdle Rate Applied', type: 'boolean' },
  applyPermitHurdleRate: {
    header: 'Permit Hurdle Rate Applied',
    type: 'boolean',
  },
  ltdBlend: { header: 'LTD Blend', type: 'boolean' },
  ntdBlend: { header: 'NTD Blend', type: 'boolean' },
  ducTypeCurveBlend: { header: 'DUC Type Curve Blend', type: 'boolean' },
  permitTypeCurveBlend: { header: 'Permit Type Curve Blend', type: 'boolean' },
  infillIncludeUpside: { header: 'Infill Include Upside', type: 'boolean' },
  applyCommissionAdjustment: {
    header: 'Commission Adjustment Applied',
    type: 'boolean',
  },
  applyShutInLimit: { header: 'Shut-In Limit Applied', type: 'boolean' },

  // ─── Decimal columns (2 decimal places) ───
  waterCutPercentage: {
    header: 'Water Cut (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  nglDifferentialPercentage: {
    header: 'NGL Differential Percentage (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  gasShrinkPercentage: {
    header: 'Gas Shrink Percentage (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  percentageCostFirstProductionDate: {
    header: 'First Prod Date DC Cost Per Well Rate (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  percentageCostSpudDate: {
    header: 'Spud Date DC Cost Per Well Rate (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  netOil: {
    header: 'Net Oil (bbl)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  netGasSold: {
    header: 'Net Gas Sold (mcf)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  netNgl: {
    header: 'Net NGL (bbl)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  netWater: {
    header: 'Net Water (bbl)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  water: {
    header: 'Water (bbl)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  wellProportionInDevArea: {
    header: 'Well Proportion in Dev Area',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },
  volumeAdjustmentRatio: {
    header: 'Volume Adjustment Ratio',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },
  volumesScalingFactor: {
    header: 'Volumes Scaling Factor',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },
  wellProducingLength: {
    header: 'Well Producing Length',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },
  subtractNetAcreage: {
    header: 'Subtract Net Acreage',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  typeCurveHeaderLateralLengthFt: {
    header: 'TC Lateral Length (ft)',
    type: 'decimal',
    format: { maxDecimalPlaces: 0 },
  },
  royaltyPercentage: {
    header: 'Royalty Percentage (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  orriPercentage: {
    header: 'ORRI Percentage (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  royaltyRate: {
    header: 'Royalty Rate',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },
  wellCount: {
    header: 'Well Count',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  waterGasRatioInBblPerMmcf: {
    header: 'Water/Gas Ratio (bbl/Mmcf)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  infillFormationLimitPercentage: {
    header: 'Infill Formation Limit (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  infillDevelopmentAreaLimitPercentage: {
    header: 'Infill Dev Area Limit (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  targetPDPDiscountRate: {
    header: 'Target PDP Discount Rate (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  targetLTDDiscountRate: {
    header: 'Target LTD Discount Rate (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  targetNTDDiscountRate: {
    header: 'Target NTD Discount Rate (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  targetDUCDiscountRate: {
    header: 'Target DUC Discount Rate (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  targetPermitDiscountRate: {
    header: 'Target Permit Discount Rate (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  maxPDPDiscountRate: {
    header: 'Max PDP Discount Rate (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  maxLTDDiscountRate: {
    header: 'Max LTD Discount Rate (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  maxNTDDiscountRate: {
    header: 'Max NTD Discount Rate (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  maxDUCDiscountRate: {
    header: 'Max DUC Discount Rate (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  maxPermitDiscountRate: {
    header: 'Max Permit Discount Rate (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 2 },
  },
  riskFactorWi: {
    header: 'Title Risk (WI)',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },
  riskFactorRi: {
    header: 'Title Risk (RI)',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },
  commissionTargetPercentage: {
    header: 'Commission Target (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },
  commissionMaxPercentage: {
    header: 'Commission Max (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },

  // ─── Decimal columns (4 decimal places - tax/interest rates) ───
  oilTaxPercentage: {
    header: 'Oil Tax Percentage (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },
  gasTaxPercentage: {
    header: 'Gas Tax Percentage (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },
  nglTaxPercentage: {
    header: 'NGL Tax Percentage (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },
  adValoremTaxPercentage: {
    header: 'Ad Valorem Tax Percentage (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },
  ltdHurdleRate: {
    header: 'LTD Hurdle Rate (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },
  ducHurdleRate: {
    header: 'DUC Hurdle Rate (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },
  permitHurdleRate: {
    header: 'Permit Hurdle Rate (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },

  // ─── Decimal columns (10 decimal places - interest percentages) ───
  netRevenueInterest: {
    header: 'Net Revenue Interest (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 10 },
  },
  netWorkingInterest: {
    header: 'Net Working Interest (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 10 },
  },
  nriSource: {
    header: 'NRI source',
    type: 'text',
  },
  wiSource: {
    header: 'WI source',
    type: 'text',
  },
  // ─── Integer columns (0 decimal places) ───
  nglYield: {
    header: 'NGL Yield (bbl/Mmcf)',
    type: 'integer',
  },
  grossOil: {
    header: 'Gross Oil (bbl)',
    type: 'integer',
  },
  grossGas: {
    header: 'Gross Gas (mcf)',
    type: 'integer',
  },
  grossGasSold: {
    header: 'Gross Gas Sold (mcf)',
    type: 'integer',
  },
  grossNgl: {
    header: 'Gross NGL (bbl)',
    type: 'integer',
  },
  grossWater: {
    header: 'Gross Water (bbl)',
    type: 'integer',
  },
  slotNumber: { header: 'Slot Number', type: 'integer' },
  month: { header: 'Month', type: 'integer' },
  newWellsOnline: { header: 'New Wells Online', type: 'integer' },
  spudToProductionMonths: {
    header: 'Spud to Production (months)',
    type: 'integer',
  },
  permitToProductionMonths: {
    header: 'Permit to Production (months)',
    type: 'integer',
  },
  ltdPermitToProductionMonths: {
    header: 'LTD Permit to Production (months)',
    type: 'integer',
  },
  ltdDrillStartMonths: { header: 'LTD Drill Start (months)', type: 'integer' },
  ltdDrillOffsetMonths: {
    header: 'LTD Drill Offset (months)',
    type: 'integer',
  },
  ltdAverageWellDrilledYears: {
    header: 'LTD Avg Well Drilled (years)',
    type: 'integer',
  },
  ntdDUCStartMonth: { header: 'NTD DUC Start Month', type: 'integer' },
  ntdDUCEndMonth: { header: 'NTD DUC End Month', type: 'integer' },
  ducStartMonth: { header: 'DUC Start Month', type: 'integer' },
  ducEndMonth: { header: 'DUC End Month', type: 'integer' },
  permitStartMonth: { header: 'Permit Start Month', type: 'integer' },
  permitEndMonth: { header: 'Permit End Month', type: 'integer' },
  infillLTDLimit: { header: 'Infill LTD Limit', type: 'integer' },
  shutInLimit: { header: 'Shut-In Limit', type: 'integer' },
  yearsInProduction: { header: 'Years in Production', type: 'integer' },

  // ─── Currency columns (2 decimal places) ───
  oilWellheadPrice: {
    header: 'Oil Price ($/bbl)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  gasWellheadPrice: {
    header: 'Gas Price ($/MMBTU)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  oilDifferentialPrice: {
    header: 'Oil Differential Price ($/bbl)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  gasDifferentialPrice: {
    header: 'Gas Differential Price ($/MMBTU)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  oilRealizedPrice: {
    header: 'Oil Realized Price ($/bbl)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  gasRealizedPrice: {
    header: 'Gas Realized Price ($/MMBTU)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  nglRealizedPrice: {
    header: 'NGL Realized Price ($/bbl)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  grossOilRevenue: {
    header: 'Gross Oil Revenue ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  grossGasRevenue: {
    header: 'Gross Gas Revenue ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  grossNglRevenue: {
    header: 'Gross NGL Revenue ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  grossRevenue: {
    header: 'Gross Revenue ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netOilRevenue: {
    header: 'Net Oil Revenue ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netGasRevenue: {
    header: 'Net Gas Revenue ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netNglRevenue: {
    header: 'Net NGL Revenue ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netRevenue: {
    header: 'Net Revenue ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netOilTaxAmount: {
    header: 'Net Oil Tax Amount ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netGasTaxAmount: {
    header: 'Net Gas Tax Amount ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netNglTaxAmount: {
    header: 'Net NGL Tax Amount ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netAdValoremTaxAmount: {
    header: 'Net Ad Valorem Amount ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  conservationTaxPercentage: {
    header: 'Conservation Tax (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },
  grossConservationTaxAmount: {
    header: 'Gross Conservation Tax ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netConservationTaxAmount: {
    header: 'Net Conservation Tax ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  productionOtherTaxPercentage: {
    header: 'Production/Other Tax (%)',
    type: 'decimal',
    format: { maxDecimalPlaces: 4 },
  },
  grossProductionOtherTaxAmount: {
    header: 'Gross Production/Other Tax ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netProductionOtherTaxAmount: {
    header: 'Net Production/Other Tax ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  oilOpexCost: {
    header: 'Oil Opex Rate ($/bbl)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  gasOpexCost: {
    header: 'Gas Opex Rate ($/mcf)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  waterOpexCost: {
    header: 'Water Opex Rate ($/bbl)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netOilOpexCost: {
    header: 'Net Oil Opex Cost ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netGasOpexCost: {
    header: 'Net Gas Opex Cost ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netWaterOpexCost: {
    header: 'Net Water Opex Cost ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netFirstProductionDateDcCostPerWell: {
    header: 'Net First Prod Date DC Cost Per Well ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netSpudDateDcCostPerWell: {
    header: 'Net Spud Date DC Cost Per Well ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netLoeFixedWellCost: {
    header: 'Net LOE Cost Per Well ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  afeDrillingCosts: {
    header: 'AFE Drilling Costs ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  afeCompletionCosts: {
    header: 'AFE Completion Costs ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  afeFacilitiesCosts: {
    header: 'AFE Facilities Costs ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  afeContingenciesCosts: {
    header: 'AFE Contingencies Costs ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  afeOtherCosts: {
    header: 'AFE Other Costs ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  afeUwi: {
    header: 'AFE UWI',
    type: 'text',
  },
  afeUWI: {
    header: 'AFE UWI',
    type: 'text',
  },
  afeWellName: {
    header: 'AFE Well Name',
    type: 'text',
  },
  afeLateralLength: {
    header: 'AFE Lateral Length',
    type: 'integer',
    format: { maxDecimalPlaces: 0 },
  },
  applyAfe: {
    header: 'Apply AFE',
    type: 'boolean',
  },
  grossCosts: {
    header: 'Gross Costs ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netCosts: {
    header: 'Net Costs ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  leaseholdCashflow: {
    header: 'Leasehold Cashflow ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  grossCashflow: {
    header: 'Gross Cashflow ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netCashflow: {
    header: 'Net Cashflow ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  firstProductionDateDcCostPerWell: {
    header: 'First Prod Date DC Cost ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  spudDateDcCostPerWell: {
    header: 'Spud Date DC Cost ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  loeFixedWellCost: {
    header: 'LOE Fixed Well Cost ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  grossFixedRevenue: {
    header: 'Gross Fixed Revenue ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  grossVariableRevenueCosts: {
    header: 'Gross Variable Revenue Costs ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netVariableRevenueCosts: {
    header: 'Net Variable Revenue Costs ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  grossOilTaxAmount: {
    header: 'Gross Oil Tax ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  grossGasTaxAmount: {
    header: 'Gross Gas Tax ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  grossNglTaxAmount: {
    header: 'Gross NGL Tax ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  grossRevenueAfterCommodityTaxes: {
    header: 'Gross Revenue After Commodity Taxes ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netRevenueAfterCommodityTaxes: {
    header: 'Net Revenue After Commodity Taxes ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  adValoremTaxAmount: {
    header: 'Ad Valorem Tax ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  grossRevenueAfterTax: {
    header: 'Gross Revenue After Tax ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netRevenueAfterTax: {
    header: 'Net Revenue After Tax ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  grossGasOpexCost: {
    header: 'Gross Gas Opex Cost ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  grossOilOpexCost: {
    header: 'Gross Oil Opex Cost ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  grossWaterOpexCost: {
    header: 'Gross Water Opex Cost ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  grossFixedCosts: {
    header: 'Gross Fixed Costs ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },
  netFixedCosts: {
    header: 'Net Fixed Costs ($)',
    type: 'currency',
    format: { maxDecimalPlaces: 2 },
  },

  // ─── Currency columns (0 decimal places) ───
  // Note: minDecimalPlaces must be <= maxDecimalPlaces, so set both to 0
  dcCostPerWell: {
    header: 'DC Cost Per Well ($)',
    type: 'currency',
    format: { minDecimalPlaces: 0, maxDecimalPlaces: 0 },
  },
  loeCostPerWellYear1: {
    header: 'LOE Cost Per Well Yr 1 ($)',
    type: 'currency',
    format: { minDecimalPlaces: 0, maxDecimalPlaces: 0 },
  },
  loeCostPerWellYear2: {
    header: 'LOE Cost Per Well Yr 2 ($)',
    type: 'currency',
    format: { minDecimalPlaces: 0, maxDecimalPlaces: 0 },
  },
  loeCostPerWellYear3: {
    header: 'LOE Cost Per Well Yr 3 ($)',
    type: 'currency',
    format: { minDecimalPlaces: 0, maxDecimalPlaces: 0 },
  },
  loeCostPerWellYear4: {
    header: 'LOE Cost Per Well Yr 4 ($)',
    type: 'currency',
    format: { minDecimalPlaces: 0, maxDecimalPlaces: 0 },
  },
  loeCostPerWellYear5AndUp: {
    header: 'LOE Cost Per Well Yr 5+ ($)',
    type: 'currency',
    format: { minDecimalPlaces: 0, maxDecimalPlaces: 0 },
  },
};

// ─────────────────────────────────────
// Filter Transformation
// ─────────────────────────────────────

/**
 * Transform TanStack Table's ColumnFiltersState to server's CashflowsFilterConfig format.
 * Handles different filter types:
 * - Text: string → { column, op: 'contains', value }
 * - Date range: [start, end] → gte/lte filters
 * - Numeric range: [min, max] → gte/lte filters
 * @param {ColumnFiltersState} filters - TanStack Table column filters state
 * @return {CashflowsFilterConfig[]} Server-compatible filter config array
 */
const transformFiltersForServer = (
  filters: ColumnFiltersState
): CashflowsFilterConfig[] => {
  const serverFilters: CashflowsFilterConfig[] = [];

  for (const filter of filters) {
    const { id: column, value } = filter;

    // Skip if no value
    if (value === undefined || value === null || value === '') {
      continue;
    }

    // Handle range filters (date or numeric) - value is [min/start, max/end]
    if (Array.isArray(value) && value.length === 2) {
      const [min, max] = value as [unknown, unknown];

      if (min !== null && min !== undefined && min !== '') {
        serverFilters.push({
          column,
          op: 'gte',
          value: min as string | number,
        });
      }

      if (max !== null && max !== undefined && max !== '') {
        serverFilters.push({
          column,
          op: 'lte',
          value: max as string | number,
        });
      }
    }
    // Handle text filters - string value uses 'contains' operator (ILIKE)
    else if (typeof value === 'string') {
      serverFilters.push({
        column,
        op: 'contains',
        value,
      });
    }
    // Handle exact match for other types (boolean, enum, etc.)
    else {
      serverFilters.push({
        column,
        op: 'eq',
        value: value as string | number | boolean,
      });
    }
  }

  return serverFilters;
};

/**
 * Transform TanStack Table's SortingState to server's CashflowsSortConfig format.
 * TanStack: { id: string, desc: boolean }[]
 * Server: { column: string, dir: 'asc' | 'desc' }[]
 * @param {SortingState} sorting - TanStack Table sorting state
 * @return {CashflowsSortConfig[]} Server-compatible sort config array
 */
const transformSortForServer = (
  sorting: SortingState
): CashflowsSortConfig[] => {
  return sorting.map((sort) => ({
    column: sort.id,
    dir: sort.desc ? 'desc' : 'asc',
  }));
};

/**
 * Get column metadata from config, with fallback defaults.
 * Returns the meta object to pass to DataTable column definition.
 * @param {string} columnKey - The column key to get metadata for
 * @return {DataTableColumnMeta} The meta object for DataTable column definition
 */
const getColumnMeta = (columnKey: string): DataTableColumnMeta => {
  const config = columnConfig[columnKey];

  if (config) {
    const meta: DataTableColumnMeta = {};
    if (config.type) {
      meta.type = config.type;
    }
    if (config.format) {
      meta.format = config.format;
    }
    return meta;
  }

  // Default: treat as decimal with 2 decimal places for unconfigured numeric columns
  // This ensures any column not in config still gets reasonable formatting
  return { type: 'decimal', format: { maxDecimalPlaces: 2 } };
};

/**
 * Get column header from config, with fallback to startCase.
 * @param {string} columnKey - The column key to get header for
 * @return {string} The header string for the column
 */
const getColumnHeader = (columnKey: string): string => {
  const config = columnConfig[columnKey];
  return config?.header || startCase(columnKey.replace(/_/g, ' '));
};

// ─────────────────────────────────────
// Props
// ─────────────────────────────────────

type PaginatedProps = {
  calculationId?: string;
  evaluationId?: number | string;
  effectiveDate?: string;
  pageSize?: number;
  exportPresets?: ExportPreset[];
};

// Type alias for data table row that extends Record<string, unknown>
type CashflowTableRow = CashflowRow & Record<string, unknown>;

// ─────────────────────────────────────
// Column Builder
// ─────────────────────────────────────

/**
 * Build TanStack column definitions dynamically from data.
 * Uses the columnConfig for type and format settings.
 * @param {CashflowRow | undefined} sampleRow - A sample row to extract column keys from
 * @return {DataTableColumnDef<CashflowTableRow>[]} Array of TanStack column definitions
 */
const buildColumns = (
  sampleRow: CashflowRow | undefined
): DataTableColumnDef<CashflowTableRow>[] => {
  if (!sampleRow) return [];

  // Fixed columns that always appear first
  const fixedColumns: DataTableColumnDef<CashflowTableRow>[] = [
    {
      id: 'date',
      accessorKey: 'date',
      header: 'Date',
      meta: { type: 'date' },
    },
    {
      id: 'slotRef',
      accessorKey: 'slotRef',
      header: 'Slot Ref',
      // Note: Don't set meta.type when using custom cell renderer
      // because table-body uses formatCellValue() when type is defined
      cell: ({ getValue }) => {
        const value = getValue() as string | undefined;
        if (!value) {
          return '';
        }

        // Only create link for numeric slotRef values (wells have numeric IDs)
        const isNumeric = /^\d+$/.test(value);
        if (!isNumeric) {
          return value;
        }

        return (
          <a
            href={`/wells/${value}`}
            className={style.tableHyperlink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {value}
          </a>
        );
      },
    },
    {
      id: 'slotCategory',
      accessorKey: 'slotCategory',
      header: 'Slot Category',
      // Note: Don't set meta.type when using custom cell renderer
      cell: ({ getValue }) => {
        const value = getValue() as string | undefined;
        return value?.toUpperCase() || '';
      },
    },
  ];

  // Get all other columns from the sample row, excluding wellHref (internal link, not user-facing)
  const dataColumnKeys = Object.keys(sampleRow).filter(
    (key) =>
      key !== 'date' &&
      key !== 'slotRef' &&
      key !== 'slotCategory' &&
      key !== 'wellHref'
  );

  // Build data columns with proper types from config. The Development Area
  // and UWI columns get hyperlinks (new tab) — mirrors the slotRef wells
  // link above. Skip `meta.type` so DataTable's body uses the custom cell
  // directly (formatCellValue() takes over when meta.type is set, which
  // would override the link).
  const dataColumns: DataTableColumnDef<CashflowTableRow>[] =
    dataColumnKeys.map((key) => {
      if (key === 'developmentAreaName') {
        return {
          id: key,
          accessorKey: key,
          header: getColumnHeader(key),
          cell: ({ getValue }) => {
            const value = getValue() as string | undefined;
            if (!value) return '';
            return (
              <a
                href={`/development_areas/${encodeURIComponent(value)}`}
                className={style.tableHyperlink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {value}
              </a>
            );
          },
        };
      }
      if (key === 'uwi') {
        return {
          id: key,
          accessorKey: key,
          header: getColumnHeader(key),
          cell: ({ getValue }) => {
            const value = getValue() as string | undefined;
            if (!value) return '';
            return (
              <a
                href={`/wells/${encodeURIComponent(value)}`}
                className={style.tableHyperlink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {value}
              </a>
            );
          },
        };
      }
      return {
        id: key,
        accessorKey: key,
        header: getColumnHeader(key),
        meta: getColumnMeta(key),
      };
    });

  return [...fixedColumns, ...dataColumns];
};

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Paginated cashflows table with server-side pagination and infinite scroll.
 * @return {JSX.Element} The rendered table component
 */
export const FreeCashFlowsBySlotTablePaginated = ({
  calculationId,
  evaluationId,
  pageSize = 500,
  exportPresets: cashflowsColumnOrderConfigs = [],
}: PaginatedProps) => {
  const {
    rows,
    isLoading,
    isFetchingMore,
    error,
    hasMore,
    fetchNextPage,
    setSearch,
    setFilters,
    setSort,
    currentSort,
    currentFilters,
  } = useCashflowsByMonthPaginated({
    calculationId,
    serverPageSize: pageSize,
  });

  // Handle column filter changes - transform to server format and update
  const handleFilterChange = useCallback(
    (filters: ColumnFiltersState) => {
      const serverFilters = transformFiltersForServer(filters);
      setFilters(serverFilters);
    },
    [setFilters]
  );

  // Handle sorting changes - transform to server format and update
  const handleSortChange = useCallback(
    (sorting: SortingState) => {
      const serverSort = transformSortForServer(sorting);
      setSort(serverSort);
    },
    [setSort]
  );

  // Store last valid columns so they persist when data is empty (e.g., after filtering)
  // This prevents the table structure from disappearing when filters return no results
  const lastValidColumnsRef = useRef<DataTableColumnDef<CashflowTableRow>[]>(
    []
  );

  // Build columns from first row, falling back to last known columns when no data
  const columns = useMemo(() => {
    const newColumns = buildColumns(rows[0]);
    if (newColumns.length > 0) {
      lastValidColumnsRef.current = newColumns;
      return newColumns;
    }
    // Fall back to last known columns when no data (e.g., empty filter results)
    return lastValidColumnsRef.current;
  }, [rows]);

  // Cast rows to the table row type
  const tableData = rows as CashflowTableRow[];

  const [exportError, setExportError] = useState<string | null>(null);

  const exportSubscriptionRef = useRef<ReturnType<typeof subscribe> | null>(
    null
  );

  // Queues a server-side export and routes the Pusher completion event
  // back into a browser download (or surfaces the error).
  const handleServerExport = useCallback(
    async ({
      columnPresetName,
      columns,
      filenameSuffix,
    }: {
      columnPresetName?: string;
      columns?: string[];
      filenameSuffix?: string;
    }) => {
      if (!calculationId) return;

      try {
        const requestBody: Record<string, unknown> = {
          calculationId,
        };
        if (currentFilters.length > 0) {
          requestBody.filters = currentFilters;
        }
        if (currentSort.length > 0) {
          requestBody.sort = currentSort;
        }
        if (columns && columns.length > 0) {
          // Translate frontend column ids (camelCase) to the parquet schema
          // (snake_case) the cashflows backend reads from.
          requestBody.columns = columns.map((id) => snakeCase(id));
        } else if (columnPresetName) {
          requestBody.columnPresetName = columnPresetName;
        }
        if (filenameSuffix) {
          requestBody.filenameSuffix = filenameSuffix;
        }

        const response = await fetch(
          apiConfig.AGGREGATED_FREE_CASHFLOW_EXPORT_URL,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Export failed:', response.status, errorText);
          throw new Error(`Export failed: ${response.status}`);
        }

        const exportResponse = await response.json();
        const exportId = exportResponse.exportId;

        // Clean up any existing subscription before creating a new one
        if (exportSubscriptionRef.current) {
          exportSubscriptionRef.current.unbind('cashflows_export_complete');
          exportSubscriptionRef.current.unsubscribe();
        }

        // Subscribe to Pusher channel to receive the download URL when export completes
        const channel = `eval_calculator_${calculationId}`;
        const subscription = subscribe(channel);
        exportSubscriptionRef.current = subscription;

        subscription.bind(
          'cashflows_export_complete',
          (data: {
            export_id: string;
            calculation_id: string;
            status: 'complete' | 'failed';
            download_url?: string;
            error?: string;
            row_count?: number;
            file_count?: number;
            file_size_bytes?: number;
          }) => {
            // Only handle events for this specific export
            if (data.export_id !== exportId) {
              return;
            }

            // Clean up subscription
            subscription.unbind('cashflows_export_complete');
            subscription.unsubscribe();
            exportSubscriptionRef.current = null;

            if (data.status === 'complete' && data.download_url) {
              // Trigger the download
              window.open(data.download_url, '_blank', 'noopener,noreferrer');
            } else if (data.status === 'failed') {
              console.error('Export failed:', data.error);
              setExportError(data.error || 'Export failed. Please try again.');
            }
          }
        );

        // Show a brief notification that export is in progress
        // The download will auto-trigger when complete
        console.log(
          `Export ${exportId} queued for calculation ${calculationId}`
        );
      } catch (err) {
        console.error('Failed to queue export:', err);
        setExportError('Failed to start export. Please try again.');
      }
    },
    [calculationId, currentFilters, currentSort]
  );

  // Translate server-API presets into rich data-table presets. The fallback
  // (when the eval-presets hook hasn't resolved yet) is a single default
  // "Export" entry, fed through the same map so the rich-preset shape isn't
  // duplicated.
  const enrichedPresets = useMemo<DataTableExportPreset[]>(() => {
    const sourcePresets: ExportPreset[] =
      cashflowsColumnOrderConfigs.length > 0
        ? cashflowsColumnOrderConfigs
        : [{ name: 'Export', formats: ['csv'] }];

    return sourcePresets.map((serverPreset) => ({
      name: serverPreset.name,
      format: 'csv',
      filename: `eval_${evaluationId}_cashflows_by_month`,
      columns: serverPreset.isCurrentView ? 'visible' : undefined,
      onExport: (_format, _filters, columnPresetName, columns) =>
        handleServerExport({
          columnPresetName,
          columns,
          filenameSuffix: serverPreset.filenameSuffix,
        }),
    }));
  }, [cashflowsColumnOrderConfigs, evaluationId, handleServerExport]);

  // Show placeholder when no calculation is selected
  if (!calculationId && !rows.length) {
    return <div className={style.noData}>No calculation selected</div>;
  }

  // Show skeleton until first data load provides columns for DataTable to initialize with.
  // TODO: Move this guard into DataTable so all dynamic-column tables benefit automatically.
  if (isLoading && columns.length === 0) {
    return (
      <DataTableSkeleton
        columnCount={5}
        rowCount={10}
        showHeader={false}
        showToolbar={false}
      />
    );
  }

  return (
    <div className={style.freeCashFlowsBySlotTableWrapper}>
      {exportError && (
        <InlineNotification
          kind="error"
          title={exportError}
          lowContrast
          onCloseButtonClick={() => setExportError(null)}
        />
      )}
      <DataTable<CashflowTableRow>
        data={tableData}
        columns={columns}
        getRowId={(row) => `${row.date}-${row.slotRef}-${row.slotCategory}`}
        isLoading={isLoading || isFetchingMore}
        error={error}
        scroll={{
          mode: 'infinite',
          hasNextPage: hasMore,
        }}
        onLoadMore={() => fetchNextPage()}
        onGlobalSearchChange={(s) => setSearch(s.globalFilter)}
        onFilterChange={(s) => handleFilterChange(s.columnFilters)}
        onSortChange={(s) => handleSortChange(s.sorting)}
        config={{
          globalSearch: true,
          columnFilters: true,
          columnVisibility: true,
          sorting: true,
          columnResizing: true,
          columnReordering: true,
          columnPinning: true,
          export: { exportPresets: enrichedPresets },
        }}
        persistenceKey={`cashflows-by-month-${evaluationId}`}
      />
    </div>
  );
};
